import React, { useEffect, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import PropTypes from 'prop-types';

import Text from '../../Text/Text';
import getCarMakeList from '../../../thunks/general/getCarMakeList';
import getCompatibilityModelGroup, {
  setCompatibilityModelGroup
} from '../../../thunks/Compatibility/getCompatibilityModelGroup';
import getCompatibilityYearGroup, {
  setCompatibilityYearGroup
} from '../../../thunks/Compatibility/getCompatibilityYearGroup';
import prepareSelectedVehicleObject, {
  prepareVehicleAccordionObject, setMake, setModel, setYear,
} from '../../../utils/compatibilityHelper';
import CarSelectors from './CarSelectors';
import { mergeObjects } from '../../../utils/helpers';
import Accordion from '../../Accordion/Accordion';
import { setCompatibilityList } from '../../../actions/vehicleCompatibility/vehicleCompatibilityList';
import CarNoteModal from './CarNoteModal';

const VehicleSelector = () => {
  const dispatch = useDispatch();
  const [carNoteModal, setCarNoteModal] = useState(false);
  const {
    carMakeList, carMakeListLoading,
    modelGroup, modelGroupLoading,
    yearGroup, yearGroupLoading,
    compatibilityCarsGroup, vehicleCompatibilityList
  } = useSelector((state) => ({
    carMakeList: state?.getCarMakeList?.list,
    carMakeListLoading: state?.getCarMakeList?.loading,
    modelGroup: state?.getCompatibilityModelGroup?.modelGroup,
    modelGroupLoading: state?.getCompatibilityModelGroup?.loading,
    yearGroup: state?.getCompatibilityYearGroup?.yearGroup,
    yearGroupLoading: state?.getCompatibilityYearGroup?.loading,
    compatibilityCarsGroup: state?.getCompatibilityCarsGroup.carsGroup,
    compatibilityCarsGroupLoading: state?.getCompatibilityCarsGroup.loading,
    vehicleCompatibilityList: state?.vehicleCompatibilityList.data
  }), shallowEqual);

  useEffect(() => {
    if (compatibilityCarsGroup) {
      dispatch(setCompatibilityList(mergeObjects(vehicleCompatibilityList, compatibilityCarsGroup)));
    }
  }, [compatibilityCarsGroup]);

  const { carList } = useSelector((state) => ({
    carList: state?.getListingCompatibilityList.data,
  }), shallowEqual);

  useEffect(() => {
    dispatch(setCompatibilityList(carList || {}));
    if (Object?.keys(carList || {}).length) {
      batch(() => {
        dispatch(getCompatibilityModelGroup({
          make: Object?.keys(carList)
        }));
        dispatch(getCompatibilityYearGroup({
          make: Object?.keys(carList),
          model: Object?.keys(carList)
            ?.map((make) => Object?.keys(carList[make])
              ?.map((model) => model)).reduce((acc, curr) => [...acc, ...curr], [])
        }));
      });
    }
  }, [carList]);

  const selectedVehicles = prepareSelectedVehicleObject(vehicleCompatibilityList);

  useEffect(() => {
    if (!carMakeList.length && !carMakeListLoading) {
      dispatch(getCarMakeList());
    }
    dispatch(setCompatibilityModelGroup([]));
    dispatch(setCompatibilityYearGroup([]));
  }, []);

  const accordionData = prepareVehicleAccordionObject(vehicleCompatibilityList,
    (data) => dispatch(setCompatibilityList(data)));

  function updateSelected(obj) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          if ('selected' in value) {
            return [key, { ...value, selected: !value.selected }];
          }
            return [key, updateSelected(value)];
        }
        return [key, value];
      })
    );
  }

  function updateNote(obj, note) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          if ('selected' in value) {
            return [key, { ...value, car_note: note }];
          }
            return [key, updateNote(value, note)];
        }
        return [key, value];
      })
    );
  }

  const onReverse = () => {
    const updatedData = updateSelected(vehicleCompatibilityList);
    dispatch(setCompatibilityList(updatedData));
  };

  const onSaveNote = (note) => {
    const updatedData = updateNote(vehicleCompatibilityList, note);
    dispatch(setCompatibilityList(updatedData));
    setCarNoteModal(false);
  };

  return (
    <div className="w-[1100px]">
      <Text text="Start by selecting one or more makes, models and years" muted />
      <br />
      <div className="flex flex-row justify-around">
        <div className="flex-1 mr-[15px] last:mr-0">
          <CarSelectors
            label={`Make:(${selectedVehicles.makes.length})`}
            options={carMakeList}
            value={selectedVehicles.makes}
            isDisabled={carMakeListLoading}
            onChange={(value, remove) => setMake(
              value, remove,
              vehicleCompatibilityList,
              modelGroup, dispatch
            )}
          />
        </div>
        <div className="flex-1 mr-[15px] last:mr-0">
          <CarSelectors
            label={`Model:(${selectedVehicles.model.length})`}
            options={modelGroup}
            value={selectedVehicles.model}
            isDisabled={modelGroupLoading}
            onChange={(value, remove) => setModel(value, remove,
              vehicleCompatibilityList,
              modelGroup, dispatch)}
          />
        </div>
        <div className="flex-1 mr-[15px] last:mr-0">
          <CarSelectors
            label={`Year:(${selectedVehicles.years.length})`}
            options={yearGroup}
            value={selectedVehicles.years}
            isDisabled={yearGroupLoading}
            onChange={(value, remove) => {
              setYear(value, remove, vehicleCompatibilityList, dispatch);
            }}
            maxSelectedValue={100}
          />
        </div>
      </div>
      {!!selectedVehicles?.years?.length
        && (
        <>
          <br />
          <div className="flex flex-row justify-between">
            <Text text="Now select your vehicle trims" muted />
            <div className="flex flex-row gap-[16px]">
              <span
                className="globalLink"
                onClick={onReverse}
                role="presentation"
              >
                Reverse
              </span>
              <span
                className="globalLink"
                onClick={() => setCarNoteModal(true)}
                role="presentation"
              >
                Note for all
              </span>
            </div>
          </div>
          <br />
          <Accordion
            data={accordionData}
          />
          <CarNoteModal
            visible={carNoteModal}
            toggleVisible={() => setCarNoteModal(false)}
            onSave={(note) => onSaveNote(note)}
            note=""
          />
        </>
      )}
    </div>
  );
};

VehicleSelector.propTypes = {
  values: PropTypes.shape({
    carMakes: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string
    })),
    carModel: PropTypes.shape({
      value: PropTypes.string
    }),
    carYear: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string
      })
    ),
  }),
};

VehicleSelector.defaultProps = {
  values: {}
};

export default VehicleSelector;
